import { Link } from "react-router-dom";
import {
    Wrench,
    Compass,
    Users,
    Umbrella,
    Coins,
    Eye,
    ArrowRight,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import GuardianMark from "../components/GuardianMark";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";

/* ──────────────────────────────────────────────────────────────────
 *  /why-roofs-fail — Problem framing that leads to Guardianship™.
 * ────────────────────────────────────────────────────────────────── */

const EYEBROW =
    "font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]";
const GOLD_RULE = "block h-[3px] w-16 bg-srm-gold";

const PROBLEMS = [
    {
        icon: Wrench,
        title: "Reactive maintenance",
        body: "In most buildings roof spend follows the leak, not the wear that caused it. By the time water shows on a ceiling the underlying issue has usually been running for months.",
    },
    {
        icon: Compass,
        title: "No long-term plan",
        body: "Without a documented plan, roof spend becomes unpredictable, sinking funds get drawn at the wrong time, and AGM decisions are made without the technical evidence to support them.",
    },
    {
        icon: Users,
        title: "Contractors checking contractors",
        body: "It's common practice for the company that carries out repairs to also inspect and recommend them \u2014 which quietly turns every recommendation into a quote in waiting.",
    },
    {
        icon: Umbrella,
        title: "Insurance disputes",
        body: "Storm claims are won or lost on documentation. Without an independent condition baseline, damage cannot be separated from pre-existing wear, and insurers push back.",
    },
    {
        icon: Coins,
        title: "Unexpected capital expenditure",
        body: "Roof failures rarely give notice. Committees can find themselves reviewing six-figure works on short timelines, without an independent scope to compare against.",
    },
    {
        icon: Eye,
        title: "No independent verification",
        body: "When works are signed off by the same party that carried them out, committees end up trusting the invoice rather than the roof itself.",
    },
];

const CONSEQUENCES = [
    "Recurring leaks and internal water damage",
    "Owners paying twice for the same repair",
    "Committee members exposed to personal criticism",
    "Insurance premium hikes and coverage gaps",
    "Roof life shortened by 30 – 50 %",
    "Emergency levies at the wrong time in the plan",
];

function Hero() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="why-fail-hero" className="bg-white">
            <div className="container-srm py-24 lg:py-32">
                <div className="reveal max-w-[820px] mx-auto text-center">
                    <div className={EYEBROW}>The Pattern</div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <h1
                        data-testid="why-fail-headline"
                        className="mt-8 font-display font-bold text-[2.4rem] sm:text-[3rem] lg:text-[3.75rem] tracking-[-0.028em] leading-[1.05] text-srm-navy text-balance"
                    >
                        Understanding how roof failure happens.
                    </h1>
                    <p className="mt-8 text-[17px] lg:text-[19px] text-srm-body leading-[1.75] max-w-[640px] mx-auto">
                        Roof failure is rarely sudden. It follows a pattern that has
                        more to do with industry structure than any individual
                        decision — and once the pattern is visible, it becomes
                        avoidable.
                    </p>
                </div>
            </div>
        </section>
    );
}

function Problems() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="why-fail-problems"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-20 lg:py-28">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Six recurring patterns</div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.75rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        The pattern behind almost every avoidable roof failure.
                    </h2>
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
                    {PROBLEMS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <article
                                key={p.title}
                                data-testid={`why-fail-card-${i}`}
                                className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-9 lg:p-10 shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)]"
                                style={{ transitionDelay: `${(i % 3) * 80}ms` }}
                            >
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold transition-all duration-500 group-hover:w-full"
                                />
                                <span className="inline-flex h-[60px] w-[60px] items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold transition-all duration-500 group-hover:bg-srm-gold/10">
                                    <Icon className="h-[26px] w-[26px]" strokeWidth={1.5} />
                                </span>
                                <h3 className="mt-7 font-display font-bold text-srm-navy text-[1.22rem] lg:text-[1.32rem] tracking-[-0.008em] leading-snug">
                                    {p.title}
                                </h3>
                                <p className="mt-4 text-[14.5px] lg:text-[15px] text-srm-body leading-[1.75]">
                                    {p.body}
                                </p>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function Consequences() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="why-fail-consequences"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-[42fr_58fr] gap-12 lg:gap-20 items-start">
                    <div className="reveal">
                        <div className={EYEBROW}>What the pattern costs</div>
                        <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                        <h2 className="mt-8 font-display font-bold text-[1.95rem] sm:text-[2.3rem] lg:text-[2.55rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                            The consequences are rarely dramatic. They are cumulative.
                        </h2>
                        <p className="mt-6 text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.8] max-w-[480px]">
                            Small industry defaults compound over time — quietly eroding
                            the asset, the budget and the committee&apos;s
                            confidence. Recognising the pattern is the first step
                            toward avoiding it.
                        </p>
                    </div>

                    <ul className="reveal space-y-4">
                        {CONSEQUENCES.map((c) => (
                            <li
                                key={c}
                                className="flex items-start gap-4 rounded-2xl bg-white ring-1 ring-srm-line p-5 lg:p-6 shadow-[0_4px_18px_rgba(15,40,86,0.05)]"
                            >
                                <span
                                    aria-hidden
                                    className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-srm-gold"
                                />
                                <span className="text-[14.5px] lg:text-[15.5px] text-srm-navy leading-[1.55]">
                                    {c}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function Bridge() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="why-fail-bridge"
            className="relative overflow-hidden"
            style={{ backgroundColor: "#163E7A" }}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full"
                style={{
                    background:
                        "radial-gradient(circle, rgba(201,165,92,0.10) 0%, rgba(201,165,92,0) 70%)",
                }}
            />
            <div className="container-srm relative py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <span aria-hidden className={`mx-auto ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2.1rem] sm:text-[2.6rem] lg:text-[3.15rem] tracking-[-0.024em] leading-[1.08] text-white text-balance">
                        Once you can see the pattern, you can design around it.
                    </h2>
                    <p className="mt-8 text-[16px] lg:text-[17.5px] text-white/80 leading-[1.85] max-w-2xl mx-auto">
                        The{" "}
                        <GuardianMark
                            term="Guardian Program"
                            tone="light"
                            variant="signature"
                            className="text-srm-gold"
                        />{" "}
                        is SRM&apos;s answer — continuous, independent care of your
                        roof asset across its full life.
                    </p>
                    <div className="mt-12 flex justify-center">
                        <Link
                            to="/guardian-program"
                            data-testid="why-fail-cta-primary"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[56px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300"
                        >
                            Explore the Guardian Program
                            <ArrowRight
                                className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
                                strokeWidth={2}
                            />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function WhyRoofsFail() {
    usePageMeta({
        title: "Why Roofs Fail \u2014 The Path to the Guardian Program\u2122 | SRM",
        description:
            "Reactive maintenance, no lifecycle planning and contractors checking contractors — the six recurring failures behind almost every avoidable strata roof failure, and how SRM's Guardian Program solves them.",
        path: "/why-roofs-fail",
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Why Roofs Fail", url: "/why-roofs-fail" },
        ],
    });

    return (
        <div data-testid="why-fail-page" className="min-h-screen bg-white">
            <Navbar />
            <main id="main">
                <Hero />
                <Problems />
                <Consequences />
                <Bridge />
            </main>
            <Footer />
        </div>
    );
}
